import type { Core } from 'cytoscape';
import type { SimulationProcessNode } from '../types';

const VIEWPORT_PADDING = 60;
const FIT_PADDING = 120;
const CENTER_DURATION = 220;
const FIT_DURATION = 280;
const MAX_FOLLOW_ZOOM = 1.5;
const MIN_FOLLOW_ZOOM = 0.25;
const CENTER_THRESHOLD = 0.72;

const isRenderableElement = (element: any) =>
  !!element &&
  element.length > 0 &&
  element.data('display') !== 'none' &&
  !element.data('isMarkerLabel');

const animateViewport = (
  cy: Core,
  animation: Record<string, unknown>,
  duration: number
) =>
  new Promise<void>(resolve => {
    let resolved = false;
    const finish = () => {
      if (resolved) return;
      resolved = true;
      resolve();
    };

    cy.animate(animation, {
      duration,
      easing: 'ease-in-out',
      complete: finish,
    });

    window.setTimeout(finish, duration + 32);
  });

const clampViewportZoom = (cy: Core, elements: any) => {
  const currentZoom = cy.zoom();
  if (currentZoom > MAX_FOLLOW_ZOOM) {
    cy.zoom(MAX_FOLLOW_ZOOM);
    cy.center(elements);
    return;
  }

  if (currentZoom < MIN_FOLLOW_ZOOM) {
    cy.zoom(MIN_FOLLOW_ZOOM);
    cy.center(elements);
  }
};

const getPhaseFocusElements = (
  cy: Core,
  process: SimulationProcessNode,
  phase: 'input' | 'output'
) => {
  const edges = phase === 'input' ? process.inputEdges : process.outputEdges;
  let collection = cy.collection();
  const processNode = cy.getElementById(process.id);

  if (isRenderableElement(processNode)) {
    collection = collection.union(processNode);
  }

  edges.forEach(edge => {
    const cyEdge = cy.getElementById(edge.id);
    const sourceNode = cy.getElementById(edge.sourceId);
    const targetNode = cy.getElementById(edge.targetId);

    if (isRenderableElement(cyEdge)) {
      collection = collection.union(cyEdge);
    }
    if (isRenderableElement(sourceNode)) {
      collection = collection.union(sourceNode);
    }
    if (isRenderableElement(targetNode)) {
      collection = collection.union(targetNode);
    }
  });

  return collection;
};

export const ensureProcessStepVisible = async (
  cy: Core,
  process: SimulationProcessNode,
  phase: 'input' | 'output'
) => {
  const focusElements = getPhaseFocusElements(cy, process, phase);
  if (!focusElements || focusElements.length === 0) return;

  const viewport = cy.extent();
  const bb = focusElements.boundingBox({ includeLabels: true });
  const fullyVisible =
    bb.x1 >= viewport.x1 + VIEWPORT_PADDING &&
    bb.x2 <= viewport.x2 - VIEWPORT_PADDING &&
    bb.y1 >= viewport.y1 + VIEWPORT_PADDING &&
    bb.y2 <= viewport.y2 - VIEWPORT_PADDING;

  if (fullyVisible) return;

  const canCenterAtCurrentZoom =
    bb.w <= viewport.w * CENTER_THRESHOLD &&
    bb.h <= viewport.h * CENTER_THRESHOLD;

  // 小范围越界时仅平移，尽量保持用户当前缩放；超出较多时再局部 fit。
  if (canCenterAtCurrentZoom) {
    await animateViewport(
      cy,
      {
        center: { eles: focusElements },
        zoom: cy.zoom(),
      },
      CENTER_DURATION
    );
    return;
  }

  await animateViewport(
    cy,
    {
      fit: {
        eles: focusElements,
        padding: FIT_PADDING,
      },
    },
    FIT_DURATION
  );
  clampViewportZoom(cy, focusElements);
};
